import { useEffect, useRef, useState } from 'react'

const REFRESH_THRESHOLD = 80
const PULL_RESISTANCE = 0.4
const MAX_PULL = 100

export function usePullToRefresh(onRefresh: () => unknown): { pull: number; refreshing: boolean } {
  const [pull, setPull] = useState(0)
  const [refreshing, setRefreshing] = useState(false)
  const gesture = useRef<{ startX: number; startY: number; distance: number } | null>(null)
  const refreshingRef = useRef(false)
  const onRefreshRef = useRef(onRefresh)
  onRefreshRef.current = onRefresh

  useEffect(() => {
    const onTouchStart = (event: TouchEvent): void => {
      if (event.touches.length !== 1 || window.scrollY > 0) return
      if (event.target instanceof Element && event.target.closest('[role="dialog"], [role="alertdialog"]')) {
        return
      }
      const touch = event.touches[0]
      gesture.current = { startX: touch.clientX, startY: touch.clientY, distance: 0 }
    }

    const onTouchMove = (event: TouchEvent): void => {
      const start = gesture.current
      const touch = event.touches[0]
      if (!start || !touch || refreshingRef.current) return
      const dy = touch.clientY - start.startY
      const dx = Math.abs(touch.clientX - start.startX)
      start.distance = dy > 0 && dy > dx ? Math.min(dy * PULL_RESISTANCE, MAX_PULL) : 0
      setPull(start.distance)
    }

    const onTouchEnd = (): void => {
      const start = gesture.current
      gesture.current = null
      if (!start || start.distance < REFRESH_THRESHOLD) {
        setPull(0)
        return
      }
      refreshingRef.current = true
      setRefreshing(true)
      void Promise.resolve(onRefreshRef.current()).finally(() => {
        refreshingRef.current = false
        setRefreshing(false)
        setPull(0)
      })
    }

    document.addEventListener('touchstart', onTouchStart, { passive: true })
    document.addEventListener('touchmove', onTouchMove, { passive: true })
    document.addEventListener('touchend', onTouchEnd, { passive: true })
    return () => {
      document.removeEventListener('touchstart', onTouchStart)
      document.removeEventListener('touchmove', onTouchMove)
      document.removeEventListener('touchend', onTouchEnd)
    }
  }, [])

  return { pull, refreshing }
}
